import React, { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, Loader2, Eye, EyeOff, Images } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from '../ui/alert-dialog';
import MultiUploadField from './MultiUploadField';
import { fetchProjects, adminCreateProject, adminUpdateProject, adminDeleteProject, adminReorderProjects, resolveImg } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

const blank = { title: '', category: '', location: '', description: '', image: '', images: [], published: true };

const ProjectsPanel = () => {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setItems(await fetchProjects(true)); } catch (e) { setItems([]); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(blank); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ title: p.title, category: p.category, location: p.location, description: p.description, image: p.image || '', images: (p.images?.length ? p.images : (p.image ? [p.image] : [])), published: p.published }); setOpen(true); };

  const save = async () => {
    if (!form.title.trim()) { toast({ title: 'Title is required', variant: 'destructive' }); return; }
    setSaving(true);
    try {
      const payload={...form,image:form.images?.[0]||form.image||''};
      if (editing) await adminUpdateProject(editing.id, payload);
      else await adminCreateProject(payload);
      toast({ title: editing ? 'Project updated' : 'Project added' });
      setOpen(false);
      load();
    } catch (e) { toast({ title: 'Save failed', variant: 'destructive' }); } finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await adminDeleteProject(id); toast({ title: 'Project deleted' }); load(); } catch (e) { toast({ title: 'Delete failed', variant: 'destructive' }); }
  };

  const togglePublish = async (p) => {
    setItems((prev) => prev.map((x) => (x.id === p.id ? { ...x, published: !x.published } : x)));
    try { await adminUpdateProject(p.id, { ...p, published: !p.published }); } catch (e) { load(); }
  };

  const move = async (index, dir) => {
    const next = [...items];
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setItems(next);
    try { await adminReorderProjects(next.map((x) => x.id)); } catch (e) { load(); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-semibold text-[#123020]">Projects & Gallery</h1>
          <p className="text-[#5a6b60] mt-1 text-[14px]">{items.length} project{items.length !== 1 ? 's' : ''} · use arrows to reorder</p>
        </div>
        <Button onClick={openNew} className="bg-[#86c93c] hover:bg-[#75b52f] text-[#123020] font-semibold"><Plus className="mr-2 h-4 w-4" /> Add Project</Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24 text-[#5a6b60]"><Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading...</div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 text-[#5a6b60]"><Images className="h-12 w-12 mb-3 text-[#a9b7a0]" /><p>No projects yet. Add your first one.</p></div>
      ) : (
        <div className="space-y-4">
          {items.map((p, i) => (
            <div key={p.id} className="bg-white rounded-md p-4 shadow-sm ring-1 ring-[#e6e4d7] flex gap-4 items-center">
              <div className="h-20 w-28 rounded-md overflow-hidden bg-[#eef0e8] shrink-0">
                {p.image ? <img src={resolveImg(p.image)} alt={p.title} className="h-full w-full object-cover" /> : null}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-[#123020] truncate">{p.title}</h3>
                  {!p.published && <span className="text-[11px] px-2 py-0.5 rounded bg-gray-100 text-gray-500">Unpublished</span>}
                </div>
                <p className="text-[13px] text-[#5f9e2c]">{p.category}</p>
                <p className="text-[13px] text-[#8a9980] truncate">{p.location}</p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => move(i, -1)} disabled={i === 0} className="h-8 w-8 flex items-center justify-center rounded text-[#5a6b60] hover:bg-[#eef5e6] disabled:opacity-30" title="Move up"><ArrowUp className="h-4 w-4" /></button>
                <button onClick={() => move(i, 1)} disabled={i === items.length - 1} className="h-8 w-8 flex items-center justify-center rounded text-[#5a6b60] hover:bg-[#eef5e6] disabled:opacity-30" title="Move down"><ArrowDown className="h-4 w-4" /></button>
                <button onClick={() => togglePublish(p)} className="h-8 w-8 flex items-center justify-center rounded text-[#5a6b60] hover:bg-[#eef5e6]" title={p.published ? 'Unpublish' : 'Publish'}>{p.published ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}</button>
                <button onClick={() => openEdit(p)} className="h-8 w-8 flex items-center justify-center rounded text-[#5a6b60] hover:bg-[#eef5e6]" title="Edit"><Pencil className="h-4 w-4" /></button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button className="h-8 w-8 flex items-center justify-center rounded text-red-500 hover:bg-red-50" title="Delete"><Trash2 className="h-4 w-4" /></button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete this project?</AlertDialogTitle>
                      <AlertDialogDescription>“{p.title}” will be permanently removed from your website.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => remove(p.id)} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-[560px] max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Edit Project' : 'Add Project'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <MultiUploadField value={form.images || []} onChange={(images) => setForm((f) => ({ ...f, images, image: images[0] || '' }))} />
            <div><Label className="font-medium">Title</Label><Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className="mt-1.5" placeholder="Rooftop Garden" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="font-medium">Category</Label><Input value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} className="mt-1.5" placeholder="Rooftop & Terrace" /></div>
              <div><Label className="font-medium">Location</Label><Input value={form.location} onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))} className="mt-1.5" placeholder="Manhattan, New York" /></div>
            </div>
            <div><Label className="font-medium">Description</Label><Textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} rows={3} className="mt-1.5 resize-none" placeholder="Short description of the project" /></div>
            <div className="flex items-center justify-between rounded-md bg-[#f5f3ea] px-4 py-3">
              <div><p className="font-medium text-[#123020]">Published</p><p className="text-[12px] text-[#8a9980]">Show this project on the website</p></div>
              <Switch checked={form.published} onCheckedChange={(v) => setForm((f) => ({ ...f, published: v }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[#86c93c] hover:bg-[#75b52f] text-[#123020] font-semibold">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProjectsPanel;
